import { Controller } from "stimulus"
import SignaturePad from "signature_pad/dist/signature_pad.js"
import { trimCanvasToDataUrl } from "./shared_modules/signature_trim_module.js"

export const SIGNATURE_WIDTH = 400
export const SIGNATURE_HEIGHT = 150

export default class extends Controller {
  static targets = [
    "canvas",
    "uploadInput",
    "hiddenInput",
    "preview",
    "previewWrapper",
    "drawTab",
    "uploadTab",
    "drawPanel",
    "uploadPanel",
    "removeCheckbox",
    "status"
  ]

  static values = {
    width: { type: Number, default: SIGNATURE_WIDTH },
    height: { type: Number, default: SIGNATURE_HEIGHT },
    existingUrl: String
  }

  connect () {
    this.setupCanvas()
    this.bindFormSubmit()
    this.showDrawMode()

    if (this.hasExistingUrlValue && this.existingUrlValue) {
      this.showExistingSignature(this.existingUrlValue)
    }
  }

  disconnect () {
    if (this.form && this.boundHandleFormSubmit) {
      this.form.removeEventListener("submit", this.boundHandleFormSubmit)
    }
  }

  bindFormSubmit () {
    this.form = this.element.closest("form")
    if (!this.form) return

    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)
    this.form.addEventListener("submit", this.boundHandleFormSubmit)
  }

  setupCanvas () {
    const canvas = this.canvasTarget
    canvas.width = this.widthValue
    canvas.height = this.heightValue

    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: "rgb(255, 255, 255)",
      penColor: "rgb(0, 0, 0)"
    })

    const markStrokeEnd = () => {
      if (!this.signaturePad.isEmpty()) {
        this.markDirty()
        this.hidePreview()
      }
    }

    canvas.addEventListener("mouseup", markStrokeEnd)
    canvas.addEventListener("touchend", markStrokeEnd)
  }

  showDrawMode () {
    this.drawPanelTarget.classList.remove("hidden")
    this.uploadPanelTarget.classList.add("hidden")
    this.setActiveTab(this.drawTabTarget, this.uploadTabTarget)
  }

  showUploadMode () {
    this.drawPanelTarget.classList.add("hidden")
    this.uploadPanelTarget.classList.remove("hidden")
    this.setActiveTab(this.uploadTabTarget, this.drawTabTarget)
  }

  setActiveTab (activeTab, inactiveTab) {
    activeTab.classList.add("bg-primary-500", "text-white", "border-blue-500")
    activeTab.classList.remove("border-transparent", "text-gray-700")

    inactiveTab.classList.remove("bg-primary-500", "text-white", "border-blue-500")
    inactiveTab.classList.add("border-transparent", "text-gray-700")
  }

  clear () {
    this.signaturePad.clear()
    this.hidePreview()

    if (this.hasUploadInputTarget) {
      this.uploadInputTarget.value = ""
    }

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = ""
    }

    if (this.hasRemoveCheckboxTarget) {
      this.removeCheckboxTarget.checked = false
    }

    this.setStatus("Signature cleared. Save your profile to apply changes.")
  }

  async handleUpload (event) {
    const file = event.target.files[0]
    if (!file) return

    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      this.setStatus("Please upload a PNG or JPEG image.")
      event.target.value = ""
      return
    }

    try {
      const dataUrl = await this.resizeImageFile(file)
      this.signaturePad.clear()
      await this.loadDataUrlOntoPad(dataUrl)
      this.markDirty()
      this.showPreview(dataUrl)
      this.setStatus("Image loaded. Save your profile to store your signature.")
    } catch (_error) {
      this.setStatus("Unable to process the uploaded image.")
      event.target.value = ""
    }
  }

  loadDataUrlOntoPad (dataUrl) {
    return new Promise((resolve) => {
      this.signaturePad.fromDataURL(dataUrl, {
        width: this.widthValue,
        height: this.heightValue
      })
      setTimeout(resolve, 150)
    })
  }

  resizeImageFile (file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (event) => {
        const img = new Image()

        img.onload = () => {
          const canvas = document.createElement("canvas")
          canvas.width = this.widthValue
          canvas.height = this.heightValue

          const ctx = canvas.getContext("2d")
          ctx.fillStyle = "#ffffff"
          ctx.fillRect(0, 0, canvas.width, canvas.height)

          const scale = Math.min(canvas.width / img.width, canvas.height / img.height)
          const width = img.width * scale
          const height = img.height * scale
          const x = (canvas.width - width) / 2
          const y = (canvas.height - height) / 2

          ctx.drawImage(img, x, y, width, height)
          resolve(trimCanvasToDataUrl(canvas))
        }

        img.onerror = reject
        img.src = event.target.result
      }

      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  async handleFormSubmit (event) {
    if (this.hasRemoveCheckboxTarget && this.removeCheckboxTarget.checked) {
      return
    }

    if (this.signaturePad.isEmpty()) {
      return
    }

    event.preventDefault()

    const dataUrl = trimCanvasToDataUrl(this.canvasTarget)
    const file = await this.dataUrlToFile(dataUrl, "electronic_signature.png")
    const dataTransfer = new DataTransfer()

    dataTransfer.items.add(file)
    this.hiddenInputTarget.files = dataTransfer.files

    this.form.submit()
  }

  dataUrlToFile (dataUrl, filename) {
    return fetch(dataUrl)
      .then((response) => response.blob())
      .then((blob) => new File([blob], filename, { type: "image/png" }))
  }

  showExistingSignature (url) {
    this.showPreview(url)
    this.setStatus("Current saved signature shown below.")
  }

  showPreview (url) {
    if (!this.hasPreviewTarget || !this.hasPreviewWrapperTarget) return

    this.previewTarget.src = url
    this.previewWrapperTarget.classList.remove("hidden")
  }

  hidePreview () {
    if (!this.hasPreviewWrapperTarget) return

    this.previewWrapperTarget.classList.add("hidden")

    if (this.hasPreviewTarget) {
      this.previewTarget.removeAttribute("src")
    }
  }

  markDirty () {
    if (this.hasRemoveCheckboxTarget) {
      this.removeCheckboxTarget.checked = false
    }
  }

  setStatus (message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }
}
